import { useState } from 'react';
import { Download } from 'lucide-react';
import { PDFDocument, PageSizes } from 'pdf-lib';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Dropzone } from '@/components/ui/Dropzone';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';

const SIZES: Record<string, [number, number]> = {
  Fit: [0, 0],
  A4: PageSizes.A4 as [number, number],
  Letter: PageSizes.Letter as [number, number],
  A3: PageSizes.A3 as [number, number],
  A5: PageSizes.A5 as [number, number],
};

export function ImagesToPdf() {
  const [files, setFiles] = useState<File[]>([]);
  const [pageSize, setPageSize] = useState<keyof typeof SIZES>('Fit');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [working, setWorking] = useState(false);

  async function run() {
    if (!files.length) return;
    setWorking(true);
    try {
      const doc = await PDFDocument.create();
      for (const f of files) {
        const buf = await readFileAsArrayBuffer(f);
        const isPng =
          f.type === 'image/png' || /\.png$/i.test(f.name);
        const isJpg =
          f.type === 'image/jpeg' || /\.jpe?g$/i.test(f.name);
        if (!isPng && !isJpg) {
          toast.error(`${f.name}: only PNG and JPEG supported`);
          continue;
        }
        const embedded = isPng ? await doc.embedPng(buf) : await doc.embedJpg(buf);
        let [pw, ph] = SIZES[pageSize];
        if (pw === 0) {
          pw = embedded.width;
          ph = embedded.height;
        } else if (orientation === 'landscape') {
          [pw, ph] = [ph, pw];
        }
        const page = doc.addPage([pw, ph]);
        const ratio = Math.min(pw / embedded.width, ph / embedded.height);
        const w = embedded.width * ratio;
        const h = embedded.height * ratio;
        page.drawImage(embedded, {
          x: (pw - w) / 2,
          y: (ph - h) / 2,
          width: w,
          height: h,
        });
      }
      const bytes = await doc.save();
      saveAs(bytesToBlob(bytes), 'images.pdf');
      toast.success(`PDF with ${files.length} pages saved`);
    } catch (e) {
      toast.error('Could not create PDF');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="images-to-pdf"
        title="Images → PDF"
        description="Combine images into a single PDF. PNG and JPEG, with page-size and orientation control."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <Dropzone
            accept="image/png,image/jpeg"
            multiple
            onFiles={(f) => setFiles((p) => [...p, ...f])}
            files={files}
            onRemove={(i) => setFiles((p) => p.filter((_, idx) => idx !== i))}
            hint="PNG or JPEG · order = page order"
          />
        </Card>
        <Card className="space-y-3">
          <Field label="Page size">
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value as keyof typeof SIZES)}
              options={Object.keys(SIZES).map((k) => ({ value: k, label: k }))}
            />
          </Field>
          {pageSize !== 'Fit' && (
            <Field label="Orientation">
              <Select
                value={orientation}
                onChange={(e) => setOrientation(e.target.value as typeof orientation)}
                options={[
                  { value: 'portrait', label: 'Portrait' },
                  { value: 'landscape', label: 'Landscape' },
                ]}
              />
            </Field>
          )}
          <Button
            variant="primary"
            className="w-full"
            disabled={!files.length || working}
            loading={working}
            onClick={run}
          >
            <Download size={12} />
            Build PDF
          </Button>
        </Card>
      </div>
    </div>
  );
}

export default ImagesToPdf;
